import { useFormContext } from 'react-hook-form'
import { Trash2 } from 'lucide-react'
import type { EInvoiceDraftItem } from '@/lib/api/einvoice-api'
import type { EInvoiceDraftValues } from '@/lib/schemas/einvoice-draft-schemas'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { TableCell, TableRow } from '@/components/ui/table'
import { m } from '@/lib/paraglide/messages'

export const formatDraftNumber = (value: number) =>
    value.toLocaleString('vi-VN')
const parseNumber = (value: string | number) =>
    typeof value === 'number'
        ? value
        : value.trim() === ''
          ? NaN
          : Number(value.replace(',', '.'))
// Mirrors the server: an unchanged rate keeps the saved amount, which the
// order builder may have rounded differently from amount × rate.
export const draftLineDiscount = (item: EInvoiceDraftItem, percent: number) =>
    percent === item.discount_percent
        ? item.discount_amount
        : Math.round((item.amount * percent) / 100)

export function EInvoiceDraftLineMeta({ item }: { item: EInvoiceDraftItem }) {
    return (
        <p className="mt-1 text-xs text-muted-foreground">
            {m.einvoice_draft_line_meta({
                quantity: formatDraftNumber(item.quantity),
                unit: item.unit_name,
                price: formatDraftNumber(item.unit_price),
                rate: item.vat_rate,
            })}
        </p>
    )
}

// One editable product/promotion line of the draft editor table.
export function EInvoiceDraftLineRow({
    item,
    index,
    onRemove,
}: {
    item: EInvoiceDraftItem
    index: number
    onRemove: () => void
}) {
    const form = useFormContext<EInvoiceDraftValues>()
    const vatReadOnly =
        item.item_type === 2 || ['0%', 'KCT', 'KKKNT'].includes(item.vat_rate)
    const values = form.watch(`items.${index}`)
    const percent = Number.isFinite(values.discount_percent)
        ? values.discount_percent
        : item.discount_percent
    const discount = draftLineDiscount(item, percent)
    const vat = Number.isFinite(values.vat_amount)
        ? values.vat_amount
        : item.vat_amount
    const label = (field: string) =>
        m.einvoice_draft_field_label({ field, line: item.line_number })
    const name = `items.${index}.item_name` as const
    const rate = `items.${index}.discount_percent` as const
    const tax = `items.${index}.vat_amount` as const
    return (
        <TableRow className="align-top">
            <TableCell className="pt-5 text-muted-foreground tabular-nums">
                {item.line_number}
            </TableCell>
            <TableCell className="min-w-56 whitespace-normal">
                <Input
                    id={name}
                    aria-label={label(m.einvoice_draft_item_name())}
                    {...form.register(name)}
                />
                <FieldError name={name} />
                <EInvoiceDraftLineMeta item={item} />
            </TableCell>
            <TableCell className="pt-5 text-right tabular-nums">
                {formatDraftNumber(item.amount)}
            </TableCell>
            <TableCell className="w-24 whitespace-normal">
                <Input
                    id={rate}
                    inputMode="decimal"
                    className="text-right"
                    aria-label={label(m.einvoice_draft_discount_percent())}
                    {...form.register(rate, {
                        setValueAs: parseNumber,
                        onChange: (event) => {
                            const next = parseNumber(event.target.value)
                            if (
                                Number.isFinite(next) &&
                                next >= 0 &&
                                next <= 100
                            )
                                form.setValue(
                                    tax,
                                    vatReadOnly
                                        ? 0
                                        : Math.round(
                                              ((item.amount -
                                                  draftLineDiscount(
                                                      item,
                                                      next,
                                                  )) *
                                                  (parseFloat(item.vat_rate) ||
                                                      0)) /
                                                  100,
                                          ),
                                    { shouldDirty: true, shouldValidate: true },
                                )
                        },
                    })}
                />
                <FieldError name={rate} />
            </TableCell>
            <TableCell className="pt-5 text-right tabular-nums">
                {formatDraftNumber(discount)}
            </TableCell>
            <TableCell className="w-32 whitespace-normal">
                <Input
                    id={tax}
                    inputMode="numeric"
                    className="text-right"
                    readOnly={vatReadOnly}
                    aria-label={label(m.einvoice_draft_vat_amount())}
                    {...form.register(tax, { setValueAs: parseNumber })}
                />
                <FieldError name={tax} />
                {vatReadOnly && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        {m.einvoice_draft_no_vat_short()}
                    </p>
                )}
            </TableCell>
            <TableCell className="pt-5 text-right font-medium tabular-nums">
                {formatDraftNumber(item.amount - discount + vat)}
            </TableCell>
            <TableCell className="pt-2 text-right">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    aria-label={m.einvoice_draft_remove_line_label({
                        line: item.line_number,
                    })}
                    title={m.einvoice_draft_remove_line()}
                    onClick={onRemove}
                >
                    <Trash2 aria-hidden="true" />
                </Button>
            </TableCell>
        </TableRow>
    )
}
